import React from "react";
import { useSearchParams } from "react-router-dom";

import { LIMIT_PAGE } from "Shared/Constants/app";
import styles from "./DataTablePagination.module.scss";

type Props = {
  /** Total number of records across all pages. */
  count: number;
  pageParam?: string;
  limitParam?: string;
  pageSizeOptions?: number[];
  defaultLimit?: number;
  /** Noun for the range label, e.g. "reminders". */
  itemLabel?: string;
  /** Hide the rows-per-page picker when the request ignores `limit`. */
  showPageSize?: boolean;
  /**
   * Controlled mode for pages kept in component state instead of the URL
   * (e.g. client-side paging). Pass both `page` and `onPageChange`.
   */
  page?: number;
  onPageChange?: (page: number) => void;
  disabled?: boolean;
};

const positiveInt = (value: string | null, fallback: number) => {
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : fallback;
};

const SLOTS = 7;

/**
 * Page numbers with ellipses, always SLOTS items once there are more pages
 * than that, so Prev/Next and the numbers don't move between clicks:
 * 1 2 3 4 5 … 20 · 1 … 9 10 11 … 20 · 1 … 16 17 18 19 20.
 */
export const getPageItems = (page: number, pageCount: number) => {
  const range = (from: number, to: number) =>
    Array.from({ length: to - from + 1 }, (_, index) => from + index);
  if (pageCount <= SLOTS) return range(1, pageCount);
  if (page <= 4) return [...range(1, 5), "gap" as const, pageCount];
  if (page >= pageCount - 3)
    return [1, "gap" as const, ...range(pageCount - 4, pageCount)];
  return [1, "gap" as const, page - 1, page, page + 1, "gap" as const, pageCount];
};

const Chevron = ({ direction }: { direction: "left" | "right" }) => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d={direction === "left" ? "m15 6-6 6 6 6" : "m9 6 6 6-6 6"} />
  </svg>
);

const DataTablePagination = ({
  count,
  pageParam = "page",
  limitParam = "limit",
  pageSizeOptions = [10, 20, 50],
  defaultLimit = LIMIT_PAGE,
  itemLabel = "records",
  showPageSize = true,
  page: controlledPage,
  onPageChange,
  disabled = false,
}: Props) => {
  const isControlled = controlledPage !== undefined && !!onPageChange;
  const [searchParams, setSearchParams] = useSearchParams();
  const limit = Math.min(
    positiveInt(searchParams.get(limitParam), defaultLimit),
    1000,
  );
  const pageCount = Math.max(1, Math.ceil(count / limit));
  const requestedPage = isControlled
    ? controlledPage
    : positiveInt(searchParams.get(pageParam), 1);
  const page = Math.min(Math.max(1, requestedPage), pageCount);
  const from = count === 0 ? 0 : (page - 1) * limit + 1;
  const to = Math.min(page * limit, count);

  const update = (changes: Record<string, number>) => {
    const next = new URLSearchParams(searchParams);
    Object.entries(changes).forEach(([key, value]) =>
      next.set(key, String(value)),
    );
    setSearchParams(next);
  };

  const goTo = (target: number) => {
    if (disabled || target < 1 || target > pageCount || target === page) return;
    if (isControlled) onPageChange?.(target);
    else update({ [pageParam]: target });
  };

  const sizes = pageSizeOptions.includes(limit)
    ? pageSizeOptions
    : [...pageSizeOptions, limit].sort((a, b) => a - b);

  return (
    <nav className={styles.pagination} aria-label="Pagination">
      <span className={styles.range}>
        Showing{" "}
        <b>
          {from}–{to}
        </b>{" "}
        of <b>{count}</b> {itemLabel}
      </span>
      <div className={styles.controls}>
        {showPageSize && !isControlled && (
          <label className={styles.pageSize}>
            Rows per page
            <select
              value={limit}
              onChange={(event) =>
                update({
                  [limitParam]: Number(event.target.value),
                  [pageParam]: 1,
                })
              }
            >
              {sizes.map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
          </label>
        )}
        <div className={styles.pages}>
          <button
            type="button"
            className={styles.pageButton}
            aria-label="Previous page"
            disabled={disabled || page <= 1}
            onClick={() => goTo(page - 1)}
          >
            <Chevron direction="left" />
          </button>
          {getPageItems(page, pageCount).map((item, index) =>
            item === "gap" ? (
              <span
                key={`gap-${index}`}
                className={styles.gap}
                aria-hidden="true"
              >
                …
              </span>
            ) : (
              <button
                key={item}
                type="button"
                className={styles.pageButton}
                aria-current={item === page ? "page" : undefined}
                aria-label={`Page ${item}`}
                disabled={disabled}
                onClick={() => goTo(item)}
              >
                {item}
              </button>
            ),
          )}
          <button
            type="button"
            className={styles.pageButton}
            aria-label="Next page"
            disabled={disabled || page >= pageCount}
            onClick={() => goTo(page + 1)}
          >
            <Chevron direction="right" />
          </button>
        </div>
      </div>
    </nav>
  );
};

export default DataTablePagination;
